/**
 * DebugToolConfirmation — Inline confirmation prompt for debug tool actions.
 */

import { useState } from 'react';
import { Shield } from 'lucide-react';

type DebugToolConfirmationProps = {
  requestId: string;
  toolName: string;
  description: string;
  category: string;
  /**
   * Whether a "remember for this session" grant can actually be recorded.
   *
   * The grant is keyed on `(tabId, category)`, and `tabId` is optional on every
   * one of these tools. When it is absent the main process drops the choice
   * without a word, so the checkbox has to go — a control that silently does
   * nothing teaches people their answers do not matter.
   */
  canRemember: boolean;
  onRespond: (requestId: string, approved: boolean, remember: boolean) => void;
};

const TOOL_LABELS: Record<string, string> = {
  debug_step_over: 'Step Over',
  debug_step_into: 'Step Into',
  debug_step_out: 'Step Out',
  debug_continue: 'Continue',
  debug_set_breakpoint: 'Set Breakpoint',
  debug_remove_breakpoint: 'Remove Breakpoint',
  debug_enable_breakpoint: 'Enable Breakpoint',
  debug_disable_breakpoint: 'Disable Breakpoint',
  debug_start: 'Start Debug',
  debug_stop: 'Stop Debug',
  plsql_compile: 'Compile PL/SQL',
  plsql_compile_all: 'Compile PL/SQL (spec + body)',
  plsql_compile_debug: 'Compile PL/SQL with debug symbols',
};

/**
 * Human wording for a tool with no entry above.
 *
 * The old fallback printed the raw identifier — *"Claude wants to:
 * debug_evaluate_expression"* — which is the same defect that made a COMMIT
 * approval show a connection GUID. Better to de-snake the name than to show
 * someone an internal symbol and ask them to consent to it.
 */
const humaniseToolName = (toolName: string): string =>
  toolName
    .replace(/^(debug|plsql)_/, '')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

export function DebugToolConfirmation({ requestId, toolName, description, canRemember, onRespond }: DebugToolConfirmationProps) {
  const [remember, setRemember] = useState(false);
  const label = TOOL_LABELS[toolName] ?? humaniseToolName(toolName);

  return (
    <div className="mx-2 my-1 p-2 rounded border border-amber-500/30 bg-amber-500/5" data-testid="debug-tool-confirmation">
      <div className="flex items-center gap-1.5 text-xs font-medium text-warning mb-1">
        <Shield className="w-3.5 h-3.5" />
        Claude wants to: {label}
      </div>
      <p className="text-xs text-muted-foreground mb-2">{description}</p>
      <div className="flex items-center gap-2">
        <button
          className="px-2 py-0.5 text-xs rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
          onClick={() => onRespond(requestId, true, remember)}
          data-testid="debug-confirm-allow"
        >
          Allow
        </button>
        <button
          className="px-2 py-0.5 text-xs rounded bg-muted hover:bg-muted/80 transition-colors"
          onClick={() => onRespond(requestId, false, false)}
          data-testid="debug-confirm-deny"
        >
          Deny
        </button>
        {canRemember && (
          <label className="flex items-center gap-1 text-xs text-muted-foreground ml-2 cursor-pointer">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="rounded border-border"
              data-testid="debug-confirm-remember"
            />
            Remember for session
          </label>
        )}
      </div>
    </div>
  );
}
